import React from 'react';
import PropTypes from 'prop-types';

import { App } from '@meronex/app';
import { useTheme } from '@material-ui/core/styles';
import { NoData } from '@meronex/components';
import IChevron from '@meronex/icons/fi/FiChevronRight';
import IShield from '@meronex/icons/fi/FiShield';
import ILayers from '@meronex/icons/fi/FiLayers';
import IGrid from '@meronex/icons/fi/FiGrid';
import ILifeBuoy from '@meronex/icons/fi/FiLifeBuoy';

import View from '../views/components/View';
import { Icons } from '../common/Icons';
import { can, currentRole, p } from '../common/roles';

import RolesDialog from './roles/components/RolesDialog';
import MembersDialog from './members/components/MembersDialog';
import ItemCategoriesDialog from './itemCategories/components/ItemCategoriesDialog';
import StoreCategoriesDialog from './storeCategories/components/StoreCategoriesDialog';
import SettingsItemTypesDialog from './storeItems/SettingsItemTypesDialog';
import TeamsDialog from './teams/components/TeamsDialog';
import SupportContactDialog from './siteSettings/components/SupportContactDialog';

function ConfigurationsView(props) {
  const { activeView } = props;
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [openRolesDialog, setOpenRolesDialog] = React.useState();
  const [openMembersDialog, setOpenMembersDialog] = React.useState();
  const [openAllStoreItems, setOpenAllStoreItems] = React.useState();
  const [openItemCategories, setOpenItemCategories] = React.useState();
  const [openTeamsDialog, setOpenTeamsDialog] = React.useState();
  const [openStoreCategoriesDialog, setOpenStoreCategoriesDialog] = React.useState();
  const [openSupportContactDialog, setOpenSupportContactDialog] = React.useState();

  const cardBorder = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const mutedColor = isDark ? '#94a3b8' : '#64748b';
  const textColor = isDark ? '#e2e8f0' : '#0f172a';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const accentColor = '#6366f1';

  const renderItem = (label, icon, onClick) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px 16px',
        cursor: 'pointer',
        borderRadius: '10px',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        backgroundColor: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: accentColor,
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <span style={{
        flex: 1,
        fontSize: '14px',
        fontWeight: 500,
        color: textColor,
      }}>
        {label}
      </span>
      <IChevron size={16} style={{ color: mutedColor, flexShrink: 0 }} />
    </div>
  );

  const renderSection = (title, icon, items) => (
    <div style={{
      borderRadius: '16px',
      border: `1px solid ${cardBorder}`,
      backgroundColor: cardBg,
      padding: '20px',
      marginBottom: '16px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: accentColor,
        marginBottom: '8px',
        paddingLeft: '4px',
      }}>
        {icon}
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {items}
      </div>
    </div>
  );

  return (
    <View
      viewId={'configurations'}
      activeView={activeView}
      showMenu
      icon={<Icons.Settings size={20} />}
      title={'Admin'}
      onBack={() => {
        App.eventsManager.emit('event-active-view', ConfigurationsView.name, { activeView: 'home' });
      }}>
      <div style={{
        padding: '20px',
        maxWidth: '600px',
        overflow: 'auto',
      }}>
        {can(p.globals.viewConfigurations) ? (
          <>
            {renderSection(
              'Users Management',
              <Icons.Members size={16} />,
              <>
                {renderItem('Members', <Icons.Members size={18} />, () => setOpenMembersDialog(true))}
                {renderItem('Roles', <IShield size={18} />, () => setOpenRolesDialog(true))}
              </>
            )}
            {renderSection(
              'Data Master',
              <Icons.Data size={16} />,
              <>
                {renderItem('Item Types', <ILayers size={18} />, () => setOpenAllStoreItems(true))}
                {renderItem('Item Categories', <IGrid size={18} />, () => setOpenItemCategories(true))}
                {renderItem('Store Categories', <Icons.Category size={18} />, () => setOpenStoreCategoriesDialog(true))}
              </>
            )}
            {/* Only administrators may change site-wide settings — the server
                enforces the same check, this just hides what they can't use. */}
            {currentRole().isAdmin && renderSection(
              'Site',
              <ILifeBuoy size={16} />,
              <>
                {renderItem('Support Contact', <ILifeBuoy size={18} />, () => setOpenSupportContactDialog(true))}
              </>
            )}
          </>
        ) : (
          <NoData type={'warn'} message={'No user configurations defined'} />
        )}
      </div>
      {openSupportContactDialog && (
        <SupportContactDialog
          open={openSupportContactDialog}
          onClose={() => setOpenSupportContactDialog(false)}
        />
      )}
      {openRolesDialog && (
        <RolesDialog open={openRolesDialog} onClose={() => setOpenRolesDialog(false)} />
      )}
      {openMembersDialog && (
        <MembersDialog open={openMembersDialog} onClose={() => setOpenMembersDialog(false)} />
      )}
      {openAllStoreItems && (
        <SettingsItemTypesDialog open={openAllStoreItems} onClose={() => setOpenAllStoreItems(false)} />
      )}
      {openItemCategories && (
        <ItemCategoriesDialog open={openItemCategories} onClose={() => setOpenItemCategories()} />
      )}
      {openTeamsDialog && (
        <TeamsDialog open={openTeamsDialog} onClose={() => setOpenTeamsDialog(false)} />
      )}
      {openStoreCategoriesDialog && (
        <StoreCategoriesDialog open={openStoreCategoriesDialog} onClose={() => setOpenStoreCategoriesDialog(false)} />
      )}
    </View>
  );
}
ConfigurationsView.propTypes = {
  activeView: PropTypes.string,
};

ConfigurationsView.defaultProps = {};
export default ConfigurationsView;
